import { FileRecord, HeaderMapping, PortfolioPropertiesMap } from '@amzn/awscat-portfolio-assessment-types';
import { UOMS } from '@amzn/awscat-portfolio-assessment-types';

import { OVERWRITE_COMPARISON_LIMIT, RESOURCE_TYPES_MAP } from '../common-util/resources';
import { convertToDBUOM } from '../common-util/uomConversion';
import { convertNumberToString, getKeyFromDataRow } from '../common-util/util';

export function setOverwriteCountForOneColumn(property: HeaderMapping, portfolioProperties: PortfolioPropertiesMap,
    existingDataMap: Map<string, FileRecord>, dataRows: string[][], type: string): void {
    if (RESOURCE_TYPES_MAP[type].allowDuplicates) {
        return;
    }
    property.overwriteCount = 0;
    property.idToValueMap = {};
    property.fileIdToValuesMap = {};

    const idFields: HeaderMapping[] = Object.values(portfolioProperties)
        .filter((idField: HeaderMapping) => idField.isId);
    const allIdFieldsMatched: boolean = idFields.every((idField: HeaderMapping) => !!idField.fileHeader);
    if (!allIdFieldsMatched || property.isId || !property.fileHeaderIndex) {
        return;
    }
    const uniqueKeySet: Set<string> = new Set<string>();
    if (existingDataMap && dataRows?.[0]?.length) {
        dataRows.forEach((dataRow: string[]) => {
            // If duplicate records exist, record the first one and skip the rest
            const key = getKeyFromDataRow(dataRow, idFields, type);
            if (uniqueKeySet.has(key)) {
                return;
            }
            uniqueKeySet.add(key);
            const existingRecord: FileRecord | undefined = existingDataMap.get(key);
            const existingValue: string | undefined = existingRecord?.[property.portfolioProperty]?.toString();
            const fileValue: string = dataRow[(property.fileHeaderIndex || 0) - 1];
            
            if (diff(existingValue, fileValue, property.fileUOM)) {
                property.overwriteCount = (property.overwriteCount || 0) + 1;
                if (property.overwriteCount <= OVERWRITE_COMPARISON_LIMIT) {
                    property.idToValueMap = Object.assign(property.idToValueMap || {}, {[key]: existingValue});
                    property.fileIdToValuesMap = Object.assign(property.fileIdToValuesMap || {}, {[key]: [fileValue]});
                }
            }
        });
    }
}

export function setOverwriteCounts(portfolioProperties: PortfolioPropertiesMap, existingDataMap: Map<string, FileRecord>,
    dataRows: string[][], type: string): void {
    if (RESOURCE_TYPES_MAP[type].allowDuplicates) {
        return;
    }
    const propertiesWithFileHeaderMapping = Object.values(portfolioProperties).filter(property => {
        property.overwriteCount = 0;
        property.idToValueMap = {};
        property.fileIdToValuesMap = {};
        return !!property.fileHeaderIndex && !property.isId;
    });
    const idFields: HeaderMapping[] = Object.values(portfolioProperties)
        .filter((idField: HeaderMapping) => idField.isId);
    const allIdFieldsMatched: boolean = idFields.every((idField: HeaderMapping) => !!idField.fileHeader);
    if (!allIdFieldsMatched) {
        return;
    }
    const uniqueKeySet: Set<string> = new Set<string>();
    dataRows.forEach((dataRow: string[]) => {
        const key = getKeyFromDataRow(dataRow, idFields, type);
        if (uniqueKeySet.has(key)) {
            return;
        }
        uniqueKeySet.add(key);
        const existingRecord: FileRecord | undefined = existingDataMap.get(key);
        propertiesWithFileHeaderMapping.forEach(property => {
            if (property.isId || !property.fileHeader) {
                return;
            }
            const existingValue: string = existingRecord?.[property.portfolioProperty]?.toString() || '';
            const fileValue: string = dataRow[(property.fileHeaderIndex || 0) - 1];
            if (diff(existingValue, fileValue, property.fileUOM)) {
                property.overwriteCount = (property.overwriteCount || 0) + 1;
                // Preview of differences between existing portfolio data and file data
                if (property.overwriteCount <= OVERWRITE_COMPARISON_LIMIT) {
                    property.idToValueMap = Object.assign(property.idToValueMap || {}, {[key]: existingValue});
                    property.fileIdToValuesMap = Object.assign(property.fileIdToValuesMap || {}, {[key]: [fileValue]});
                }
            }
        });
    });
}

export function clearOverwriteCounts(portfolioProperties: PortfolioPropertiesMap): void {
    Object.keys(portfolioProperties).forEach(property => {
        portfolioProperties[property].overwriteCount = 0;
        portfolioProperties[property].overwrite = true;
    });
}

export function diff(portfolioValue: string | undefined, fileValue: string, fileUOM?: UOMS): boolean {
    if (fileUOM && fileValue && !isNaN(Number(fileValue))) {
        fileValue = convertNumberToString(convertToDBUOM(Number(fileValue), fileUOM));
    }
    return portfolioValue && fileValue && portfolioValue.trim() !== fileValue.trim() || false;
}
